import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Tooltip,
  Avatar,
  Typography,
  Menu,
  MenuItem,
  Divider,
  Collapse,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Dashboard,
  Article,
  LocalOffer as TagIcon,
  Star as StarIcon,
  CollectionsBookmark as CollectionsIcon,
  Email as EmailIcon,
  Assignment as BriefingIcon,
  Campaign as ReleaseIcon,
  BarChart as BarChartIcon,
  FactCheck as PRControlsIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  Logout as LogoutIcon,
  SwapHoriz as SwapIcon,
  AdminPanelSettings as AdminIcon,
  ViewModule as CardsTestIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Palette as PaletteIcon
} from '@mui/icons-material';
import UsageCounter from '../../molecules/UsageCounter/UsageCounter';
import { useAuth } from '../../../context/AuthContext';
import { useBrand } from '../../../context/BrandContext';
import { useTheme as useDesignTheme } from '../../../design-system/providers/ThemeProvider';
import { themesList } from '../../../design-system/themes';
import { operationalElevation, operationalRadii, operationalSurfaces } from '../../../design-system/tokens';

const NAV_ICON_SIZE = 21;
const NAV_ICON_BOX_SIZE = 24;
const MINI_NAV_ITEM_SIZE = 46;

// YIQ contrast helper — returns '#000000' for light backgrounds, '#ffffff' for dark
const getContrastText = (hex) => {
  if (!hex) return '#ffffff';
  const h = hex.replace('#', '');
  const r = parseInt(h.substr(0, 2), 16);
  const g = parseInt(h.substr(2, 2), 16);
  const b = parseInt(h.substr(4, 2), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? '#000000' : '#ffffff';
};

const menuItems = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/' },
  { text: 'Artigos', icon: <Article />, path: '/articles' },
  { text: 'Coleções', icon: <CollectionsIcon />, path: '/collections' },
  { text: 'Tags', icon: <TagIcon />, path: '/tags' },
  { text: 'Favoritos', icon: <StarIcon />, path: '/favorites' },
  {
    text: 'Central de PR',
    icon: <ReleaseIcon />,
    children: [
      { text: 'Briefing', icon: <BriefingIcon />, path: '/briefing' },
      { text: 'Releases', icon: <ReleaseIcon />, path: '/releases' },
      { text: 'Controles de PR', icon: <PRControlsIcon />, path: '/pr-controls' },
    ]
  },
  { text: 'Analytics BI', icon: <BarChartIcon />, path: '/analytics' },
  { text: 'Newsletter', icon: <EmailIcon />, path: '/newsletter' }
];

const adminMenuItems = [
  { text: 'Cerds testando', icon: <CardsTestIcon />, path: '/cerds-testando' }
];

const Sidebar = ({ open, drawerWidth, miniDrawerWidth = 60, isMobile = false, onClose }) => {
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [appearanceExpanded, setAppearanceExpanded] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user, logout } = useAuth();
  const { activeBrandData, isAdminOrMensure } = useBrand();
  const {
    setTheme,
    currentTheme,
  } = useDesignTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const themeId = currentTheme.id;
  const sidebarSurfaces = isDarkMode ? operationalSurfaces.dark : operationalSurfaces.light;
  const controlRadius = `${operationalRadii.control}px`;
  const pillRadius = `${operationalRadii.pill}px`;
  const SIDEBAR_SURFACE = sidebarSurfaces.shell;
  const SIDEBAR_TEXT = theme.palette.text.primary;
  const SIDEBAR_MUTED = isDarkMode ? theme.palette.text.disabled : theme.palette.text.secondary;
  // Dark default: near-white (#E8EAED) — mirrors light default near-black (#202124)
  const SIDEBAR_ACCENT = isDarkMode ? '#E8EAED' : theme.palette.primary.main;

  // Keep CSS variables in sync with the effective accent (including default/no-custom state)
  React.useEffect(() => {
    document.documentElement.style.setProperty('--custom-accent-color', SIDEBAR_ACCENT);
    document.documentElement.style.setProperty('--custom-accent-text-color', getContrastText(SIDEBAR_ACCENT));
  }, [SIDEBAR_ACCENT]);
  const selectedBg = `var(--custom-accent-color, ${SIDEBAR_ACCENT})`;
  const selectedHoverBg = `var(--custom-accent-color, ${SIDEBAR_ACCENT})`;
  const hoverBg = alpha(SIDEBAR_TEXT, isDarkMode ? 0.025 : 0.055);
  const hoverBorder = alpha(SIDEBAR_TEXT, isDarkMode ? 0.08 : 0.12);
  const selectedShadow = 'none';
  const activeBrandName = activeBrandData?.name || 'Mensure';
  const activeBrandLogo = activeBrandData?.logo?.trim() || '';
  const activeBrandInitial = activeBrandName.charAt(0).toUpperCase();

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile && onClose) {
      onClose();
    }
  };

  const toggleGroup = (text) => {
    setExpandedGroups((current) => ({
      ...current,
      [text]: !current[text]
    }));
  };

  const handleProfileMenuOpen = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
    setAppearanceExpanded(false);
  };

  const handleProfileAction = (path) => {
    handleProfileMenuClose();
    handleNavigate(path);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
    navigate('/login');
  };

  const toggleAppearance = () => {
    setAppearanceExpanded((current) => !current);
  };

  const isItemSelected = (itemPath) => {
    const currentPath = location.pathname;
    const currentFullPath = `${location.pathname}${location.search || ''}`;
    if (currentFullPath === itemPath) return true;
    if (currentPath === itemPath) return true;
    if (itemPath === '/collections' && currentPath.startsWith('/colecoes')) return true;
    if (itemPath === '/tags' && currentPath.startsWith('/tags/')) return true;
    if (itemPath === '/newsletter' && currentPath.startsWith('/newsletter')) return true;
    if (itemPath === '/analytics' && currentPath.startsWith('/analytics')) return true;
    return false;
  };

  const isGroupSelected = (item) => (
    Array.isArray(item.children) && item.children.some(child => isItemSelected(child.path))
  );

  const renderMenuItem = (item, isSubItem = false) => {
    const hasChildren = Array.isArray(item.children) && item.children.length > 0;
    const selected = hasChildren ? isGroupSelected(item) : isItemSelected(item.path);
    const expanded = expandedGroups[item.text] ?? selected;
    const handleItemClick = () => {
      if (hasChildren) {
        if (open) {
          toggleGroup(item.text);
        } else {
          handleNavigate(item.children[0].path);
        }
        return;
      }
      handleNavigate(item.path);
    };
    const button = (
      <ListItemButton
        selected={selected}
        onClick={handleItemClick}
        sx={{
          minHeight: isSubItem ? 36 : 48,
          justifyContent: open ? 'initial' : 'center',
          px: open ? (isSubItem ? 2.5 : 1.6) : 1.5,
          py: isSubItem ? 0.4 : undefined,
          ml: open ? 0.9 : 0.75,
          mr: open && selected ? 2.1 : (open ? 0.9 : 0.75),
          mb: 0.65,
          width: open ? 'auto' : MINI_NAV_ITEM_SIZE,
          height: open ? 'auto' : MINI_NAV_ITEM_SIZE,
          minWidth: open ? 0 : MINI_NAV_ITEM_SIZE,
          borderRadius: controlRadius,
          position: 'relative',
          color: selected ? `var(--custom-accent-text-color, ${theme.palette.primary.contrastText})` : SIDEBAR_MUTED,
          backgroundColor: selected ? selectedBg : 'transparent',
          border: '1px solid transparent',
          boxShadow: selected ? selectedShadow : 'none',
          overflow: 'hidden',
          transition: 'background-color 180ms ease, color 180ms ease, border-color 180ms ease, filter 180ms ease, box-shadow 180ms ease',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: open ? 0 : '50%',
            bottom: open ? 0 : 5,
            top: open ? 0 : 'auto',
            transform: open ? 'none' : 'translateX(-50%)',
            width: open ? 4 : 18,
            height: open ? '100%' : 3,
            borderRadius: open ? '0 4px 4px 0' : pillRadius,
            backgroundColor: 'transparent',
            boxShadow: 'none',
            transition: 'background-color 180ms ease, box-shadow 180ms ease'
          },
          '&:hover': {
            backgroundColor: selected ? selectedHoverBg : hoverBg,
            filter: selected ? 'brightness(1.08)' : 'none',
            color: selected ? `var(--custom-accent-text-color, ${theme.palette.primary.contrastText})` : SIDEBAR_TEXT,
            borderColor: hoverBorder
          },
          '&.Mui-selected': {
            backgroundColor: selectedBg,
            color: `var(--custom-accent-text-color, ${theme.palette.primary.contrastText})`,
            '&:hover': { 
              backgroundColor: selectedHoverBg,
              filter: 'brightness(1.08)'
            },
            '& .MuiListItemIcon-root': { color: `var(--custom-accent-text-color, ${theme.palette.primary.contrastText})` },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            width: isSubItem ? 22 : NAV_ICON_BOX_SIZE,
            height: isSubItem ? 22 : NAV_ICON_BOX_SIZE,
            mr: open ? (isSubItem ? 2 : 2.5) : 'auto',
            ml: open ? 0 : 'auto',
            flexShrink: 0,
            justifyContent: 'center',
            alignItems: 'center',
            color: selected ? `var(--custom-accent-text-color, ${theme.palette.primary.contrastText})` : SIDEBAR_MUTED,
            opacity: selected ? 1 : 0.58,
            transition: 'color 180ms ease, opacity 180ms ease',
            '& .MuiSvgIcon-root': {
              display: 'block',
              width: isSubItem ? 20 : NAV_ICON_SIZE,
              height: isSubItem ? 20 : NAV_ICON_SIZE,
              fontSize: isSubItem ? 20 : NAV_ICON_SIZE
            }
          }}
        >
          {item.icon}
        </ListItemIcon>
        {open && (
          <ListItemText
            primary={
              <span translate="no">{item.text}</span>
            }
            primaryTypographyProps={{
              variant: isSubItem ? 'body2' : 'body1',
              color: 'inherit',
              component: 'div',
              sx: { 
                transition: 'all 0.2s',
                fontSize: isSubItem ? '0.8rem' : '0.86rem',
                fontWeight: selected ? 700 : 600,
                letterSpacing: selected ? '0.01em' : '0'
              }
            }}
            sx={{ opacity: 1 }}
          />
        )}
        {open && hasChildren && (
          expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />
        )}
      </ListItemButton>
    );

    return (
      <React.Fragment key={item.text}>
        <ListItem disablePadding>
          {open ? button : (
            <Tooltip title={<span translate="no">{item.text}</span>} placement="right">
              {button}
            </Tooltip>
          )}
        </ListItem>
        {hasChildren && open && (
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map(child => renderMenuItem(child, true))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const brandAvatar = (size = open ? 36 : 30) => (
    activeBrandLogo ? (
      <Avatar
        key={activeBrandLogo}
        src={activeBrandLogo}
        alt={activeBrandName}
        sx={{
          width: size,
          height: size,
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          flexShrink: 0
        }}
      />
    ) : (
      <Avatar
        sx={{
          width: size,
          height: size,
          bgcolor: theme.palette.background.paper,
          color: SIDEBAR_TEXT,
          border: `1px solid ${theme.palette.divider}`,
          fontSize: open ? '0.92rem' : '0.78rem',
          fontWeight: 800,
          flexShrink: 0
        }}
      >
        {activeBrandInitial}
      </Avatar>
    )
  );

  const profileButton = (
    <Box
      component="button"
      type="button"
      aria-label="abrir menu da marca ativa"
      onClick={handleProfileMenuOpen}
      sx={{
        width: open ? 'calc(100% - 28px)' : MINI_NAV_ITEM_SIZE,
        minWidth: open ? 0 : MINI_NAV_ITEM_SIZE,
        height: open ? 58 : MINI_NAV_ITEM_SIZE,
        mx: open ? 1.75 : 'auto',
        mb: 1.5,
        px: open ? 1.1 : 0,
        border: '1px solid transparent',
        borderRadius: open ? pillRadius : controlRadius,
        bgcolor: alpha(SIDEBAR_TEXT, isDarkMode ? 0.035 : 0.055),
        color: SIDEBAR_TEXT,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: open ? 'flex-start' : 'center',
        gap: 1.1,
        font: 'inherit',
        overflow: 'hidden',
        transition: 'background-color 180ms ease, border-color 180ms ease',
        '&:hover': {
          bgcolor: alpha(SIDEBAR_TEXT, isDarkMode ? 0.055 : 0.075),
          borderColor: hoverBorder
        }
      }}
    >
      {brandAvatar(open ? 36 : 30)}
      {open && (
        <>
          <Box sx={{ minWidth: 0, textAlign: 'left', flex: 1 }}>
            <Typography
              variant="body2"
              component="div"
              translate="no"
              noWrap
              sx={{
                color: SIDEBAR_TEXT,
                fontSize: '0.86rem',
                fontWeight: 800,
                lineHeight: 1.15
              }}
            >
              {activeBrandName}
            </Typography>
            <Typography
              variant="caption"
              component="div"
              noWrap
              sx={{
                color: alpha(SIDEBAR_TEXT, 0.64),
                fontSize: '0.72rem',
                fontWeight: 650,
                lineHeight: 1.05
              }}
            >
              Marca ativa
            </Typography>
          </Box>
          <ExpandMoreIcon sx={{ fontSize: 18, color: alpha(SIDEBAR_TEXT, 0.72), flexShrink: 0 }} />
        </>
      )}
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? open : true}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: isMobile ? drawerWidth : (open ? drawerWidth : miniDrawerWidth),
        flexShrink: 0,
        display: isMobile && !open ? 'none' : 'block',
        '& .MuiDrawer-paper': {
          width: isMobile ? drawerWidth : (open ? drawerWidth : miniDrawerWidth),
          boxSizing: 'border-box',
          transition: 'width 0.3s ease',
          background: SIDEBAR_SURFACE,
          borderRight: 'none',
          boxShadow: 'none',
          overflowX: 'hidden',
          zIndex: isMobile ? 1200 : 850
        },
      }}
    >
      <Box
        sx={{
          overflow: 'hidden',
          pt: 12,
          minHeight: '100%',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          '& .MuiList-root': {
            position: 'relative',
            zIndex: 1
          }
        }}
      >
        <List sx={{ flex: 1 }}>
          {/* Itens principais */}
          {menuItems.map((item) => renderMenuItem(item))}
          {isAdminOrMensure && adminMenuItems.map((item) => renderMenuItem(item))}
        </List>

        {user && (
          <>
            {open ? profileButton : (
              <Tooltip title={activeBrandName} placement="right">
                {profileButton}
              </Tooltip>
            )}
            <Menu
              anchorEl={profileAnchorEl}
              open={Boolean(profileAnchorEl)}
              onClose={handleProfileMenuClose}
              anchorOrigin={{ vertical: 'top', horizontal: open ? 'right' : 'right' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              PaperProps={{
                sx: {
                  minWidth: 280,
                  maxWidth: 320,
                  ml: 1,
                  bgcolor: theme.palette.background.paper,
                  color: SIDEBAR_TEXT,
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: operationalElevation.modal,
                  '& .MuiMenuItem-root': {
                    color: SIDEBAR_TEXT,
                    '&:hover': { bgcolor: hoverBg }
                  },
                  '& .MuiListItemIcon-root': {
                    color: SIDEBAR_MUTED
                  },
                  '& .MuiDivider-root': {
                    borderColor: theme.palette.divider
                  }
                }
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {brandAvatar(40)}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" translate="no" noWrap sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      {activeBrandName}
                    </Typography>
                    <Typography variant="body2" translate="no" color="text.secondary" noWrap sx={{ fontSize: '0.8rem' }}>
                      {user.name || 'Usuario'} conectado
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider />
              <UsageCounter variant="menuItem" />
              <Divider />

              <MenuItem onClick={() => handleProfileAction('/profile')}>
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Meu Perfil</ListItemText>
              </MenuItem>

              <MenuItem onClick={toggleAppearance}>
                <ListItemIcon>
                  <PaletteIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Aparencia</ListItemText>
                {appearanceExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </MenuItem>

              <Collapse in={appearanceExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ px: 2, py: 1.25, bgcolor: 'action.hover' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Tema
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {themesList.map(item => (
                      <Box
                        key={item.id}
                        onClick={() => setTheme(item.id)}
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          bgcolor: themeId === item.id ? 'primary.main' : 'background.paper',
                          color: themeId === item.id ? 'primary.contrastText' : 'text.primary',
                          border: '1px solid',
                          borderColor: themeId === item.id ? 'primary.main' : 'divider',
                          '&:hover': {
                            bgcolor: themeId === item.id ? 'primary.dark' : 'action.hover'
                          }
                        }}
                      >
                        {item.name}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Collapse>

              <MenuItem onClick={() => handleProfileAction('/brand-settings')}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Config. da Marca</ListItemText>
              </MenuItem>

              {isAdminOrMensure && (
                <MenuItem onClick={() => handleProfileAction('/admin')}>
                  <ListItemIcon>
                    <AdminIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Painel Admin</ListItemText>
                </MenuItem>
              )}

              <Divider />

              <MenuItem onClick={() => { window.open('https://mensure.com.br/ajuda', '_blank'); handleProfileMenuClose(); }}>
                <ListItemIcon>
                  <HelpIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Ajuda</ListItemText>
              </MenuItem>

              <MenuItem onClick={() => handleProfileAction('/brand-selection')}>
                <ListItemIcon>
                  <SwapIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Mudar de Marca</ListItemText>
              </MenuItem>

              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Sair</ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default Sidebar;
